import {
  CRMChannel,
  TicketBaseDto,
  WhatsappMessageModel,
  instagramMessages$,
  twilioMessages$,
  voiceMessages$,
  webChatBotMessages$,
} from "@jugl-web/rest-api";
import { EmailConversationDto } from "@jugl-web/rest-api/integrations/models/EmailConversation";
import {
  EmailMessageModel,
  transformEmailMessageDtoToModel,
} from "@jugl-web/rest-api/integrations/models/EmailMessage";
import { InstagramMessageModel } from "@jugl-web/rest-api/integrations/models/InstagramMessage/InstagramMessageModel";
import {
  TwilioMessageDto,
  TwilioMessageModel,
  transformTwilioMessageDtoToModel,
} from "@jugl-web/rest-api/integrations/models/TwilioMessage";
import {
  VoiceMessageDto,
  transformVoiceMessageDtoToModel,
} from "@jugl-web/rest-api/integrations/models/VoiceMessage";
import { WebChatBotMessageModel } from "@jugl-web/rest-api/integrations/models/WebChatBotMessage";
import { WebChatBotMessageDto } from "@jugl-web/rest-api/integrations/models/WebChatBotMessage/WebChatBotMessageDto";
import { transformWebChatBotMessageDtoToModel } from "@jugl-web/rest-api/integrations/models/WebChatBotMessage/utils";
import { emitTicketCommentMutationEvent } from "@jugl-web/domain-resources/tickets/events/ticketCommentMutation";
import { useApplyTicketLiveUpdate } from "@jugl-web/domain-resources/tickets/hooks/useApplyTicketLiveUpdate";
import { TicketCommentLiveUpdatePayload } from "@jugl-web/domain-resources/tickets/types/TicketCommentLiveUpdatePayload";
import { transformTicketCommentDtoToModel } from "@jugl-web/rest-api/tickets/models/TicketComment/transformTicketCommentDtoToModel";
import { useToast } from "@jugl-web/utils";
import { useLocalStorage } from "@jugl-web/utils/hooks/useStorage";
import { CRM_INBOX_PREFERENCES_KEY } from "@jugl-web/utils/storage";
import { useCRMUnreadIndicators } from "@web-src/modules/client-support/providers/CRMUnreadIndicatorsProvider";
import { useEntityProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntityProvider";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { PeopleInfoSidebar } from "@web-src/modules/people/components/PeopleInfoSidebar";
import usePhoenixSocket from "@web-src/modules/socket/providers/usePhoenixSocket";
import debounce from "lodash/debounce";
import { Channel } from "phoenix";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Subject } from "rxjs";
import { sortConnectedChannels } from "@web-src/modules/common/utils/sortConnectedChannels";
import { getChatSecondsToExpiration } from "../../components/ClientSupportChat/utils/getChatSecondsToExpiration";
import { ConversationSidePanelTab } from "../../components/ConversationSidePanel/types";
import { useManageAccountsDrawer } from "../../components/ManageAccountsDrawer/providers/ManageAccountsDrawerContext";
import {
  ChannelListItem,
  ClientSupportView,
  ConversationModel,
  CreateTicketSidebarState,
  InboxPreferencesByEntityId,
} from "../../types";
import {
  getChannelFromView,
  isCRMChannel,
  isClientSupportViewsEqual,
  isTicketFilterId,
  resolveInitialClientSupportView,
  resolveViewFromEntityPreferences,
} from "../../utils";
import { isConversationContactBlocked } from "../../utils/isConversationContactBlocked";
import { useEmailAccount } from "../EmailAccountProvider/useEmailAccount";
import { useInstagramAccount } from "../InstagramAccountProvider/useInstagramAccount";
import { useWebChatBotAccount } from "../WebChatBotAccountProvider/useWebChatBotAccount";
import { useWhatsappAccount } from "../WhatsappAccountProvider/useWhatsappAccount";
import {
  ClientSupportContext,
  ClientSupportPageContextValue,
} from "./ClientSupportPageContext";
import { useTicketDetailsSidebarState } from "./useTicketDetailsSidebarState";

type ReplyMessagesMap = Record<
  string,
  | WhatsappMessageModel
  | InstagramMessageModel
  | WebChatBotMessageModel
  | TwilioMessageModel
  | EmailMessageModel
  | undefined
>;

const CRM_INBOX_TOPIC = "crm_inbox";

export const ClientSupportPageProvider: FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { channel: urlChannel, filter: urlFilter } = useParams<string>();
  const [searchParams] = useSearchParams();

  const { entityId, entity } = useEntityProvider();
  const isCxEntity = entity?.type === "cx";

  const [selectedConversation, setSelectedConversation] =
    useState<ConversationModel>();

  const [selectedView, setSelectedView] = useState<ClientSupportView>(() =>
    resolveInitialClientSupportView({
      urlChannel,
      urlFilter,
      entityId: entityId ?? undefined,
      availableChannels: [],
    })
  );

  const [inboxPreferences, setInboxPreferences] =
    useLocalStorage<InboxPreferencesByEntityId>(CRM_INBOX_PREFERENCES_KEY, {});

  const [createTicketSidebarState, setCreateTicketSidebarState] =
    useState<CreateTicketSidebarState>({ isOpen: false });

  const {
    ticketDetailsSidebarState,
    openTicketDetailsSidebar,
    closeTicketDetailsSidebar,
  } = useTicketDetailsSidebarState();

  const [replyMessages, setReplyMessages] = useState<ReplyMessagesMap>({});
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [openedUserProfile, setOpenedUserProfile] = useState<string>();
  const [isConversationSidePanelOpen, setIsConversationSidePanelOpen] =
    useState(false);
  const [conversationSidePanelVariant, setConversationSidePanelVariant] =
    useState<ConversationSidePanelTab>();
  const [chatSecondsLeft, setChatSecondsLeft] = useState(0);

  const openConversationSidePanel = useCallback(
    (params?: { variant?: ConversationSidePanelTab }) => {
      setConversationSidePanelVariant(
        params?.variant ?? ConversationSidePanelTab.customer
      );
      setIsConversationSidePanelOpen(true);
    },
    []
  );

  const closeConversationSidePanel = useCallback(() => {
    setIsConversationSidePanelOpen(false);
  }, []);

  const isContactBlocked = useMemo(
    () => isConversationContactBlocked(selectedConversation),
    [selectedConversation]
  );

  const selectedChannel = useMemo(
    () => getChannelFromView(selectedView) ?? CRMChannel.whatsapp,
    [selectedView]
  );

  useEffect(() => {
    closeConversationSidePanel();
  }, [closeConversationSidePanel, selectedConversation?.conv_id]);

  const isExpirableChannel =
    selectedView.kind === "channel" &&
    (selectedView.channel === CRMChannel.whatsapp ||
      selectedView.channel === CRMChannel.instagram);

  const isChatExpired = useMemo(
    () => chatSecondsLeft <= 0 && isExpirableChannel,
    [chatSecondsLeft, isExpirableChannel]
  );

  // Recompute the chat window countdown when the active conversation changes.
  // Only whatsapp/instagram chats expire; email conversations also carry
  // `since`, but it must not start the expiration countdown.
  useEffect(() => {
    if (
      selectedConversation &&
      "since" in selectedConversation &&
      isExpirableChannel
    ) {
      setChatSecondsLeft(
        getChatSecondsToExpiration(selectedConversation.since)
      );
    } else {
      setChatSecondsLeft(0);
    }
  }, [selectedConversation, isExpirableChannel]);

  // Decrement the chat window countdown every second while a timed conversation is open.
  useEffect(() => {
    if (
      !selectedConversation ||
      !("since" in selectedConversation) ||
      !isExpirableChannel
    ) {
      return undefined;
    }

    const timer = setInterval(() => {
      setChatSecondsLeft((prev) => (prev > 0 ? prev - 1 : prev));
    }, 1000);

    return () => clearInterval(timer);
  }, [selectedConversation, isExpirableChannel]);

  const { socket, updatesLobbyChannel, socketConnected } = usePhoenixSocket();
  const applyTicketLiveUpdate = useApplyTicketLiveUpdate();
  const $incomingWhatsappMessages = useRef<
    ClientSupportPageContextValue["incomingWhatsappMessages$"]
  >(new Subject());
  const $incomingInstagramMessages = useRef<
    ClientSupportPageContextValue["incomingInstagramMessages$"]
  >(new Subject());
  const $incomingWebMessages = useRef<
    ClientSupportPageContextValue["incomingWebMessages$"]
  >(new Subject());
  const $incomingTwilioMessages = useRef<
    ClientSupportPageContextValue["incomingTwilioMessages$"]
  >(new Subject());
  const $incomingEmailMessages = useRef<
    ClientSupportPageContextValue["incomingEmailMessages$"]
  >(new Subject());
  const $incomingVoiceMessages = useRef<
    ClientSupportPageContextValue["incomingVoiceMessages$"]
  >(new Subject());
  const chatbotChannelRef = useRef<Channel | null>(null);
  const { toast } = useToast();
  const { navigateToPage } = useNavigation();
  const { crmConfig, isCRMConfigLoading } = useManageAccountsDrawer();
  const { whatsappAgent } = useWhatsappAccount();
  const { instagramAccount } = useInstagramAccount();
  const { webChatBotAccount } = useWebChatBotAccount();
  const { isConnected: isEmailConnected } = useEmailAccount();
  const { refetch: refetchUnreadCounts } = useCRMUnreadIndicators();

  const openCreateTicketSidebar = useCallback(
    (initialFormValues?: CreateTicketSidebarState["initialFormValues"]) => {
      setCreateTicketSidebarState({ isOpen: true, initialFormValues });
    },
    []
  );

  const closeCreateTicketSidebar = useCallback(() => {
    setCreateTicketSidebarState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const selectView = useCallback(
    (view: ClientSupportView) => {
      if (isClientSupportViewsEqual(selectedView, view)) {
        return;
      }

      if (isSendingMessage) {
        toast(
          "Please wait until the message is sent before switching conversations",
          {
            variant: "warning",
          }
        );
        return;
      }

      setSelectedView(view);
      setSelectedConversation(undefined);

      setInboxPreferences((prev) => {
        if (!entityId) {
          return prev;
        }

        return {
          ...prev,
          [entityId]: { lastView: view },
        };
      });

      if (view.kind === "channel") {
        navigateToPage(
          "clientSupportChannel",
          { channel: view.channel },
          { replacePathOnly: true }
        );
        return;
      }

      navigateToPage(
        "clientSupportTickets",
        { filter: view.filter },
        { replacePathOnly: true }
      );
    },
    [
      entityId,
      isSendingMessage,
      navigateToPage,
      selectedView,
      setInboxPreferences,
      toast,
    ]
  );

  const selectChannel = useCallback(
    (channel: CRMChannel) => {
      selectView({ kind: "channel", channel });
    },
    [selectView]
  );

  const selectConversation = useCallback(
    (conversation?: ConversationModel, auto?: boolean) => {
      if (isSendingMessage && !auto) {
        toast(
          "Please wait until the message is sent before switching conversations",
          {
            variant: "warning",
          }
        );
        return;
      }

      if (selectedView.kind !== "channel") {
        return;
      }

      setSelectedConversation(conversation);

      if (conversation) {
        navigateToPage(
          "clientSupportActiveConversation",
          {
            channel: selectedView.channel,
            activeConversation: conversation.conv_id,
          },
          { replacePathOnly: true }
        );
        return;
      }

      navigateToPage(
        "clientSupportChannel",
        { channel: selectedView.channel },
        { replacePathOnly: true }
      );
    },
    [isSendingMessage, navigateToPage, selectedView, toast]
  );

  const setReplyMessage = useCallback(
    (
      conversationId: string,
      message?:
        | WhatsappMessageModel
        | InstagramMessageModel
        | WebChatBotMessageModel
        | TwilioMessageModel
        | EmailMessageModel
    ) => {
      setReplyMessages((prev) => ({
        ...prev,
        [conversationId]: message,
      }));
    },
    []
  );

  const refetchTrigger$ = useMemo(() => new Subject<void>(), []);

  // sidebar unread counters: re-sync from the server shortly after socket
  // messages arrive on any channel
  const debouncedRefetchUnreadCounts = useMemo(
    () => debounce(() => refetchUnreadCounts(), 2000),
    [refetchUnreadCounts]
  );

  useEffect(
    () => () => debouncedRefetchUnreadCounts.cancel(),
    [debouncedRefetchUnreadCounts]
  );

  const availableChannels: ChannelListItem[] = useMemo(() => {
    if (isCRMConfigLoading) return [];

    const channels: ChannelListItem[] = [];

    if (crmConfig?.twilio?.id) {
      channels.push({
        id: CRMChannel.twilio,
      });
    }

    if (crmConfig?.voice?.id && isCxEntity) {
      channels.push({
        id: CRMChannel.voice,
      });
    }

    if (webChatBotAccount) {
      channels.push({
        id: CRMChannel.chatbot,
        name: webChatBotAccount.name,
      });
    }

    if (instagramAccount) {
      channels.push({
        id: CRMChannel.instagram,
        name: instagramAccount.name,
      });
    }

    if (whatsappAgent) {
      channels.push({
        id: CRMChannel.whatsapp,
        name: whatsappAgent.name,
      });
    }

    if (isEmailConnected) {
      channels.push({
        id: CRMChannel.email,
      });
    }

    return sortConnectedChannels(channels, crmConfig);
  }, [
    isCRMConfigLoading,
    crmConfig,
    isCxEntity,
    webChatBotAccount,
    instagramAccount,
    whatsappAgent,
    isEmailConnected,
  ]);

  const hasOAuthCallback = searchParams.has("code");

  const isBareInboxPath = !urlChannel && !urlFilter && !hasOAuthCallback;

  // Keep selectedView in sync with explicit channel or tickets segments in the URL.
  useEffect(() => {
    if (urlFilter && isTicketFilterId(urlFilter)) {
      const ticketsView: ClientSupportView = {
        kind: "tickets",
        filter: urlFilter,
      };

      setSelectedView((previousView) =>
        isClientSupportViewsEqual(previousView, ticketsView)
          ? previousView
          : ticketsView
      );
      return;
    }

    if (urlChannel && isCRMChannel(urlChannel)) {
      const channelView: ClientSupportView = {
        kind: "channel",
        channel: urlChannel,
      };

      setSelectedView((previousView) =>
        isClientSupportViewsEqual(previousView, channelView)
          ? previousView
          : channelView
      );
    }
  }, [urlChannel, urlFilter]);

  // On bare /client-support, restore the entity's last inbox view and replace the URL to match.
  useEffect(() => {
    if (!isBareInboxPath || !entityId) {
      return;
    }

    const savedView = resolveViewFromEntityPreferences(
      inboxPreferences[entityId],
      availableChannels
    );

    if (!savedView) {
      return;
    }

    setSelectedView((previousView) =>
      isClientSupportViewsEqual(previousView, savedView)
        ? previousView
        : savedView
    );

    if (savedView.kind === "channel") {
      navigateToPage(
        "clientSupportChannel",
        { channel: savedView.channel },
        { replace: true }
      );
      return;
    }

    navigateToPage(
      "clientSupportTickets",
      { filter: savedView.filter },
      { replace: true }
    );
  }, [
    availableChannels,
    entityId,
    inboxPreferences,
    isBareInboxPath,
    navigateToPage,
  ]);

  // If the selected channel was disconnected, switch to the first still-available channel.
  useEffect(() => {
    if (selectedView.kind !== "channel" || !availableChannels.length) {
      return;
    }

    const isCurrentChannelAvailable = availableChannels.some(
      (channel) => channel.id === selectedView.channel
    );

    if (!isCurrentChannelAvailable) {
      const newChannel = availableChannels[0].id;
      selectView({ kind: "channel", channel: newChannel });
    }
  }, [availableChannels, selectView, selectedView]);

  const sendWebMessage = useCallback(
    (params: { conv_id: string; conv_name: string; text: string }) => {
      const timestamp = Date.now();

      // Send via socket
      chatbotChannelRef.current?.push("send_chatbot_message", {
        conv_id: params.conv_id,
        conv_name: params.conv_name,
        payload: {
          text: params.text,
          type: "text",
          timestamp,
        },
      });
    },
    []
  );

  useEffect(() => {
    if (!socket) return () => {};

    const chatbotChannel = socket.channel("chatbot:lobby");
    chatbotChannelRef.current = chatbotChannel;

    chatbotChannel
      .join()
      .receive("ok", () => {
        // TODO: handle ok
      })
      .receive("error", () => {
        // TODO: handle error
      });

    chatbotChannel.on(
      "chatbot_message",
      ({ message }: { message: WebChatBotMessageDto }) => {
        const transformedMessage =
          transformWebChatBotMessageDtoToModel(message);

        $incomingWebMessages.current.next(transformedMessage);

        webChatBotMessages$.next({
          type: "append",
          message: transformedMessage,
        });
        debouncedRefetchUnreadCounts();
      }
    );

    return () => {
      chatbotChannel.leave();
      chatbotChannelRef.current = null;
    };
  }, [socket, debouncedRefetchUnreadCounts]);

  // CRM inbox message updates via shared updates:lobby channel.
  useEffect(() => {
    if (!updatesLobbyChannel || !socketConnected) return () => {};

    updatesLobbyChannel.push("subscribe", { topic: CRM_INBOX_TOPIC });

    const waRef = updatesLobbyChannel.on(
      "wa_message",
      ({ payload }: { payload: WhatsappMessageModel }) => {
        $incomingWhatsappMessages.current.next(payload);
        debouncedRefetchUnreadCounts();
      }
    );

    const igRef = updatesLobbyChannel.on(
      "ig_message",
      ({ payload }: { payload: InstagramMessageModel }) => {
        $incomingInstagramMessages.current.next(payload);
        instagramMessages$.next({ type: "append", message: payload });
        debouncedRefetchUnreadCounts();
      }
    );

    const emailRef = updatesLobbyChannel.on(
      "email_message",
      ({ payload }: { payload: EmailConversationDto }) => {
        $incomingEmailMessages.current.next(
          transformEmailMessageDtoToModel(payload)
        );
        debouncedRefetchUnreadCounts();
      }
    );

    const twilioRef = updatesLobbyChannel.on(
      "twilio_message",
      ({ payload }: { payload: TwilioMessageDto }) => {
        const transformedMessage = transformTwilioMessageDtoToModel(payload);
        $incomingTwilioMessages.current.next(transformedMessage);
        twilioMessages$.next({ type: "append", message: transformedMessage });
        debouncedRefetchUnreadCounts();
      }
    );

    const voiceRef = isCxEntity
      ? updatesLobbyChannel.on(
          "twilio_voice",
          ({ payload }: { payload: VoiceMessageDto }) => {
            const transformedMessage = transformVoiceMessageDtoToModel(payload);
            $incomingVoiceMessages.current.next(transformedMessage);
            voiceMessages$.next({
              type: "append",
              message: transformedMessage,
            });
            debouncedRefetchUnreadCounts();
          }
        )
      : undefined;

    const ticketCreatedRef = updatesLobbyChannel.on(
      "ticket_created",
      ({ payload }: { payload: TicketBaseDto }) => {
        applyTicketLiveUpdate("created", payload);
      }
    );

    const ticketUpdatedRef = updatesLobbyChannel.on(
      "ticket_updated",
      ({ payload }: { payload: TicketBaseDto }) => {
        applyTicketLiveUpdate("updated", payload);
      }
    );

    const ticketDeletedRef = updatesLobbyChannel.on(
      "ticket_deleted",
      ({ payload }: { payload: TicketBaseDto }) => {
        applyTicketLiveUpdate("deleted", payload);
      }
    );

    const ticketCommentCreatedRef = updatesLobbyChannel.on(
      "ticket_comment_created",
      ({ payload }: { payload: TicketCommentLiveUpdatePayload }) => {
        const { ticket_id: ticketId, ...commentDto } = payload;

        emitTicketCommentMutationEvent({
          type: "created",
          ticketId,
          comment: transformTicketCommentDtoToModel(commentDto),
        });
      }
    );

    const ticketCommentUpdatedRef = updatesLobbyChannel.on(
      "ticket_comment_updated",
      ({ payload }: { payload: TicketCommentLiveUpdatePayload }) => {
        const { ticket_id: ticketId, ...commentDto } = payload;

        emitTicketCommentMutationEvent({
          type: "updated",
          ticketId,
          comment: transformTicketCommentDtoToModel(commentDto),
        });
      }
    );

    const ticketCommentDeletedRef = updatesLobbyChannel.on(
      "ticket_comment_deleted",
      ({ payload }: { payload: TicketCommentLiveUpdatePayload }) => {
        emitTicketCommentMutationEvent({
          type: "deleted",
          ticketId: payload.ticket_id,
          commentId: payload.id,
        });
      }
    );

    return () => {
      updatesLobbyChannel.push("unsubscribe", { topic: CRM_INBOX_TOPIC });
      updatesLobbyChannel.off("wa_message", waRef);
      updatesLobbyChannel.off("ig_message", igRef);
      updatesLobbyChannel.off("email_message", emailRef);
      updatesLobbyChannel.off("twilio_message", twilioRef);
      if (voiceRef !== undefined) {
        updatesLobbyChannel.off("twilio_voice", voiceRef);
      }
      updatesLobbyChannel.off("ticket_created", ticketCreatedRef);
      updatesLobbyChannel.off("ticket_updated", ticketUpdatedRef);
      updatesLobbyChannel.off("ticket_deleted", ticketDeletedRef);
      updatesLobbyChannel.off(
        "ticket_comment_created",
        ticketCommentCreatedRef
      );
      updatesLobbyChannel.off(
        "ticket_comment_updated",
        ticketCommentUpdatedRef
      );
      updatesLobbyChannel.off(
        "ticket_comment_deleted",
        ticketCommentDeletedRef
      );
    };
  }, [
    applyTicketLiveUpdate,
    isCxEntity,
    updatesLobbyChannel,
    socketConnected,
    debouncedRefetchUnreadCounts,
  ]);

  const value: ClientSupportPageContextValue = useMemo(
    () => ({
      selectedView,
      selectView,
      selectedConversation,
      selectConversation,
      refetchTrigger$,
      replyMessage: selectedConversation
        ? replyMessages[selectedConversation.conv_id]
        : undefined,
      setReplyMessage,
      incomingWhatsappMessages$: $incomingWhatsappMessages.current,
      incomingInstagramMessages$: $incomingInstagramMessages.current,
      incomingWebMessages$: $incomingWebMessages.current,
      incomingTwilioMessages$: $incomingTwilioMessages.current,
      incomingEmailMessages$: $incomingEmailMessages.current,
      incomingVoiceMessages$: $incomingVoiceMessages.current,
      sendWebMessage,
      isSendingMessage,
      setIsSendingMessage,
      openedUserProfile,
      setOpenedUserProfile,
      selectChannel,
      selectedChannel,
      ticketDetailsSidebarState,
      openTicketDetailsSidebar,
      closeTicketDetailsSidebar,
      createTicketSidebarState,
      openCreateTicketSidebar,
      closeCreateTicketSidebar,
      availableChannels,
      isConversationSidePanelOpen,
      conversationSidePanelVariant,
      openConversationSidePanel,
      closeConversationSidePanel,
      isContactBlocked,
      isChatExpired,
      chatSecondsLeft,
    }),
    [
      selectedView,
      selectView,
      selectedConversation,
      selectConversation,
      refetchTrigger$,
      replyMessages,
      setReplyMessage,
      sendWebMessage,
      isSendingMessage,
      openedUserProfile,
      selectChannel,
      selectedChannel,
      ticketDetailsSidebarState,
      openTicketDetailsSidebar,
      closeTicketDetailsSidebar,
      createTicketSidebarState,
      openCreateTicketSidebar,
      closeCreateTicketSidebar,
      availableChannels,
      isConversationSidePanelOpen,
      conversationSidePanelVariant,
      openConversationSidePanel,
      closeConversationSidePanel,
      isContactBlocked,
      isChatExpired,
      chatSecondsLeft,
    ]
  );

  return (
    <ClientSupportContext.Provider value={value}>
      <PeopleInfoSidebar
        userId={openedUserProfile}
        isOpen={!!openedUserProfile}
        onRequestClose={() => setOpenedUserProfile(undefined)}
      />
      {children}
    </ClientSupportContext.Provider>
  );
};
